/**
 * Scout Report PDF Generation Utility
 *
 * Generates a downloadable PDF from scout report data including:
 * - Player identity and match context
 * - Parsed markdown report content (bold, horizontal rules, sections)
 * - Numeric section ratings table
 * - Overall score
 * - Footer logo on every page
 * @remarks Uses jsPDF with lazy loading to minimize bundle impact.
 * Web-only implementation -- native platforms return early.
 * Follows the same procedural PDF generation pattern as generatePlayerReportPdf.
 */

/* eslint-disable code-organization/enforce-statement-order -- PDF generation is inherently procedural; draw functions both mutate state and return Y positions for sequential layout */
/* eslint-disable functional/no-let -- PDF layout requires mutable Y tracking within draw loops */

import { format, isValid, parseISO } from "date-fns";
import { Platform } from "react-native";

import { GEMINI_LOGO_DATA_URL } from "./geminiLogoDataUrl";

/**
 * Formats an ISO date string as DD/MM/YYYY, falling through to the raw value
 * when parsing fails so malformed input is visible rather than thrown.
 * The leading YYYY-MM-DD portion is isolated before parsing so timezoned
 * inputs like "2024-01-15T00:00:00.000Z" render as the intended calendar
 * date regardless of the viewer's local timezone.
 * @param value - ISO 8601 date string (may be null/undefined)
 * @returns Formatted date, the raw value when unparseable, or empty string
 */
function formatPdfDate(value: string | null | undefined): string {
  if (!value) return "";
  const datePortion = /^(\d{4}-\d{2}-\d{2})/.exec(value)?.[1] ?? value;
  const parsed = parseISO(datePortion);
  return isValid(parsed) ? format(parsed, "dd/MM/yyyy") : value;
}

/**
 * Data required to generate a scout report PDF
 * @remarks All fields are optional except reportText, allowing graceful rendering
 * when report metadata is incomplete.
 */
export interface ScoutReportPdfData {
  /** Player name displayed prominently in the header */
  playerName?: string;
  /** Opposition team name */
  opponent?: string;
  /** Scout's own team name */
  team?: string;
  /** Match venue (home/away) */
  ground?: string;
  /** Date of the match */
  matchDate?: string;
  /** Name of the scout who authored the report */
  scoutName?: string;
  /** Date the report was created */
  reportDate?: string;
  /** Markdown-formatted report content (qnaText or translatedQnaText) */
  reportText: string;
  /** Overall performance score for the player */
  overallScore?: number;
  /** Individual section ratings from the report form */
  numericRatings?: { key: string; label: string; value: number }[];
  /** Match details for multi-match reports */
  matches?: {
    team?: string;
    opponent?: string;
    ground?: string;
    matchDate?: string;
  }[];
}

/** PDF page dimensions (A4 in points) */
const PAGE_WIDTH = 595;
const PAGE_HEIGHT = 842;
const MARGIN = 40;
const CONTENT_WIDTH = PAGE_WIDTH - MARGIN * 2;

/** Section spacing */
const SECTION_GAP = 20;
const LINE_HEIGHT = 14;

/** Colors */
const PRIMARY_COLOR: [number, number, number] = [34, 211, 238];
const TEXT_COLOR: [number, number, number] = [31, 41, 55];
const MUTED_COLOR: [number, number, number] = [107, 114, 128];
const BORDER_COLOR: [number, number, number] = [229, 231, 235];

/**
 * Checks if we need a new page and adds one if necessary
 * @param pdf - jsPDF document instance
 * @param currentY - Current Y position
 * @param requiredSpace - Space required for next content
 * @returns Y position (new page position if page break occurred)
 */
function checkPageBreak(
  pdf: import("jspdf").jsPDF,
  currentY: number,
  requiredSpace: number
): number {
  if (currentY + requiredSpace > PAGE_HEIGHT - MARGIN) {
    pdf.addPage();
    return MARGIN + 20;
  }
  return currentY;
}

/**
 * Draws a horizontal divider line
 * @param pdf - jsPDF document instance
 * @param yPos - Current Y position
 * @returns New Y position after divider
 */
function drawDivider(pdf: import("jspdf").jsPDF, yPos: number): number {
  pdf.setDrawColor(...BORDER_COLOR);
  pdf.setLineWidth(0.5);
  pdf.line(MARGIN, yPos, PAGE_WIDTH - MARGIN, yPos);
  return yPos + 10;
}

/**
 * Draws a section header with cyan accent bar
 * @param pdf - jsPDF document instance
 * @param title - Section title text
 * @param yPos - Current Y position
 * @returns New Y position after header
 */
function drawSectionHeader(
  pdf: import("jspdf").jsPDF,
  title: string,
  yPos: number
): number {
  pdf.setFillColor(...PRIMARY_COLOR);
  pdf.rect(MARGIN, yPos, 4, 16, "F");

  pdf.setFont("helvetica", "bold");
  pdf.setFontSize(12);
  pdf.setTextColor(...TEXT_COLOR);
  pdf.text(title, MARGIN + 12, yPos + 12);

  return yPos + 32;
}

/**
 * Derives location prefix from match ground value
 * @param ground - Match ground string (home/away/null)
 * @returns "vs" for home, "@" for away or unknown
 */
function getGroundPrefix(ground?: string | null): string {
  return ground?.toLowerCase() === "home" ? "vs" : "@";
}

/**
 * Draws the PDF header with player name, opponent, dates, and scout name
 * @param pdf - jsPDF document instance
 * @param data - Scout report data for header fields
 * @returns Y position after header
 */
function drawHeader(
  pdf: import("jspdf").jsPDF,
  data: ScoutReportPdfData
): number {
  // Player name (large, bold). Wrap so long names are not clipped (SE-4610).
  pdf.setFont("helvetica", "bold");
  pdf.setFontSize(22);
  pdf.setTextColor(...TEXT_COLOR);
  const titleLines: string[] = pdf.splitTextToSize(
    data.playerName ?? "Scout Report",
    CONTENT_WIDTH
  );
  const TITLE_LINE_HEIGHT = 24;
  titleLines.forEach((titleLine, index) => {
    pdf.text(titleLine, MARGIN, MARGIN + 20 + index * TITLE_LINE_HEIGHT);
  });

  let y = MARGIN + 30 + (titleLines.length - 1) * TITLE_LINE_HEIGHT;

  // Opponent + match date line. Wrap so long opposition names render in full.
  if (data.opponent || data.matchDate) {
    const parts = [
      data.opponent ? `${getGroundPrefix(data.ground)} ${data.opponent}` : null,
      data.matchDate ? formatPdfDate(data.matchDate) : null,
    ]
      .filter(Boolean)
      .join(" on ");

    pdf.setFont("helvetica", "normal");
    pdf.setFontSize(11);
    pdf.setTextColor(...MUTED_COLOR);
    const opponentLines: string[] = pdf.splitTextToSize(parts, CONTENT_WIDTH);
    opponentLines.forEach((opponentLine, index) => {
      pdf.text(opponentLine, MARGIN, y + 10 + index * LINE_HEIGHT);
    });
    y += 16 + (opponentLines.length - 1) * LINE_HEIGHT;
  }

  // Scout name + report date
  if (data.scoutName || data.reportDate) {
    const meta = [
      data.scoutName ? `Scout: ${data.scoutName}` : null,
      data.reportDate ? `Report date: ${formatPdfDate(data.reportDate)}` : null,
    ]
      .filter(Boolean)
      .join("  |  ");

    pdf.setFont("helvetica", "normal");
    pdf.setFontSize(9);
    pdf.setTextColor(...MUTED_COLOR);
    const metaLines: string[] = pdf.splitTextToSize(meta, CONTENT_WIDTH);
    metaLines.forEach((metaLine, index) => {
      pdf.text(metaLine, MARGIN, y + 10 + index * LINE_HEIGHT);
    });
    y += 16 + (metaLines.length - 1) * LINE_HEIGHT;
  }

  return drawDivider(pdf, y + 4);
}

/**
 * Draws the match details section listing individual matches
 * @param pdf - jsPDF document instance
 * @param matches - Array of match detail objects
 * @param yPos - Current Y position
 * @returns New Y position after match details
 */
function drawMatchDetails(
  pdf: import("jspdf").jsPDF,
  matches: ScoutReportPdfData["matches"],
  yPos: number
): number {
  if (!matches?.length) return yPos;

  const afterBreak = checkPageBreak(pdf, yPos, 60);
  let y = drawSectionHeader(pdf, "MATCH DETAILS", afterBreak);

  for (const match of matches) {
    y = checkPageBreak(pdf, y, 20);
    const matchText = [
      match.opponent
        ? `${getGroundPrefix(match.ground)} ${match.opponent}`
        : null,
      match.matchDate ? `on ${formatPdfDate(match.matchDate)}` : null,
    ]
      .filter(Boolean)
      .join(" ");

    pdf.setFont("helvetica", "normal");
    pdf.setFontSize(10);
    pdf.setTextColor(...TEXT_COLOR);
    // Wrap so long opposition names don't clip past the right margin (SE-4610)
    const matchIndent = MARGIN + 8;
    const matchLines: string[] = pdf.splitTextToSize(
      `\u2022 ${matchText}`,
      CONTENT_WIDTH - 8
    );
    for (const matchLine of matchLines) {
      y = checkPageBreak(pdf, y, LINE_HEIGHT);
      pdf.text(matchLine, matchIndent, y);
      y += LINE_HEIGHT;
    }
  }

  return y + SECTION_GAP / 2;
}

/**
 * Renders wrapped text lines into the PDF, advancing Y and checking page breaks
 * @param pdf - jsPDF document instance
 * @param text - Text to wrap and render
 * @param fontStyle - Font style ("normal" or "bold")
 * @param yPos - Current Y position
 * @returns New Y position after text
 */
function renderWrappedText(
  pdf: import("jspdf").jsPDF,
  text: string,
  fontStyle: "normal" | "bold",
  yPos: number
): number {
  pdf.setFont("helvetica", fontStyle);
  pdf.setFontSize(10);
  pdf.setTextColor(...TEXT_COLOR);
  const wrapped: string[] = pdf.splitTextToSize(text, CONTENT_WIDTH);
  let y = yPos;
  for (const wrapLine of wrapped) {
    y = checkPageBreak(pdf, y, LINE_HEIGHT);
    pdf.text(wrapLine, MARGIN, y);
    y += LINE_HEIGHT;
  }
  return y;
}

/**
 * Renders a single markdown line by splitting bold segments from normal text
 * @param pdf - jsPDF document instance
 * @param line - Trimmed line of text potentially containing bold markers
 * @param yPos - Current Y position
 * @returns New Y position after line
 */
function renderMarkdownLine(
  pdf: import("jspdf").jsPDF,
  line: string,
  yPos: number
): number {
  let y = checkPageBreak(pdf, yPos, LINE_HEIGHT * 2);
  const segments = line.split(/(\*\*[^*]+\*\*)/);
  let lineText = "";

  for (const segment of segments) {
    const boldMatch = /^\*\*(.+)\*\*$/.exec(segment);
    if (boldMatch) {
      if (lineText) {
        y = renderWrappedText(pdf, lineText, "normal", y);
        lineText = "";
      }
      y = renderWrappedText(pdf, boldMatch[1], "bold", y);
    } else {
      lineText += segment;
    }
  }

  if (lineText) {
    y = renderWrappedText(pdf, lineText, "normal", y);
  }

  return y;
}

/**
 * Parses report markdown text and renders it into the PDF
 *
 * Handles:
 * - Bold text wrapped in double asterisks
 * - Horizontal rules (---) as drawn lines
 * - Double newlines as section gaps
 * - Regular text with word wrapping
 * @param pdf - jsPDF document instance
 * @param text - Markdown-formatted report text
 * @param yPos - Current Y position
 * @returns New Y position after rendered text
 * @remarks Bold parsing uses a simple regex approach that handles the most common
 * markdown bold patterns. Nested or complex markdown is rendered as plain text.
 */
function drawReportContent(
  pdf: import("jspdf").jsPDF,
  text: string,
  yPos: number
): number {
  const afterBreak = checkPageBreak(pdf, yPos, 60);
  let y = drawSectionHeader(pdf, "REPORT", afterBreak);

  const lines = text.split("\n");

  for (const rawLine of lines) {
    const line = rawLine.trim();

    // Horizontal rule
    if (/^-{3,}$/.test(line)) {
      y = checkPageBreak(pdf, y, 20);
      pdf.setDrawColor(...BORDER_COLOR);
      pdf.setLineWidth(0.5);
      pdf.line(MARGIN, y, PAGE_WIDTH - MARGIN, y);
      y += 12;
      continue;
    }

    // Empty line = section gap
    if (line === "") {
      y += 8;
      continue;
    }

    y = renderMarkdownLine(pdf, line, y);
  }

  return y + SECTION_GAP;
}

/**
 * Draws numeric ratings as a label-value table
 * @param pdf - jsPDF document instance
 * @param ratings - Array of numeric rating objects
 * @param yPos - Current Y position
 * @returns New Y position after ratings table
 */
function drawNumericRatings(
  pdf: import("jspdf").jsPDF,
  ratings: NonNullable<ScoutReportPdfData["numericRatings"]>,
  yPos: number
): number {
  if (ratings.length === 0) return yPos;

  const afterBreak = checkPageBreak(pdf, yPos, 40 + ratings.length * 20);
  let y = drawSectionHeader(pdf, "SECTION SCORES", afterBreak);

  for (const rating of ratings) {
    y = checkPageBreak(pdf, y, 20);
    pdf.setFont("helvetica", "normal");
    pdf.setFontSize(10);
    pdf.setTextColor(...MUTED_COLOR);
    pdf.text(rating.label, MARGIN, y);

    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(10);
    pdf.setTextColor(...TEXT_COLOR);
    pdf.text(String(rating.value), MARGIN + 200, y);

    y += LINE_HEIGHT + 4;
  }

  return y + SECTION_GAP / 2;
}

/**
 * Draws the overall score prominently
 * @param pdf - jsPDF document instance
 * @param score - The overall score value
 * @param yPos - Current Y position
 * @returns New Y position after overall score
 */
function drawOverallScore(
  pdf: import("jspdf").jsPDF,
  score: number,
  yPos: number
): number {
  const afterBreak = checkPageBreak(pdf, yPos, 50);
  const afterHeader = drawSectionHeader(pdf, "OVERALL SCORE", afterBreak);

  pdf.setFont("helvetica", "bold");
  pdf.setFontSize(24);
  pdf.setTextColor(...PRIMARY_COLOR);
  pdf.text(String(score), MARGIN, afterHeader + 8);

  return afterHeader + 30 + SECTION_GAP;
}

/**
 * Draws footer logo on the current page
 * @param pdf - jsPDF document instance
 * @param logoDataUrl - Base64 encoded logo image data URL
 */
function drawFooterLogo(pdf: import("jspdf").jsPDF, logoDataUrl: string): void {
  try {
    const logoWidth = 24;
    const logoHeight = 24;
    const x = PAGE_WIDTH - MARGIN - logoWidth;
    const footerY = PAGE_HEIGHT - MARGIN + (MARGIN - logoHeight) / 2;
    pdf.addImage(logoDataUrl, "PNG", x, footerY, logoWidth, logoHeight);
  } catch {
    // Logo rendering failed silently -- PDF content is complete without it
  }
}

/**
 * Adds the footer logo to all pages of the document
 * @param pdf - jsPDF document instance
 * @param pageCount - Total number of pages
 * @param logoDataUrl - Base64 encoded logo image data URL
 */
function addFooterToAllPages(
  pdf: import("jspdf").jsPDF,
  pageCount: number,
  logoDataUrl: string
): void {
  for (let i = 1; i <= pageCount; i++) {
    pdf.setPage(i);
    drawFooterLogo(pdf, logoDataUrl);
  }
}

/**
 * Generates a PDF document from scout report data and triggers a browser download
 * @param data - Scout report data to render in the PDF
 * @param filename - PDF filename including .pdf extension (default: "Scout Report.pdf")
 * @returns Promise that resolves when PDF is generated and download triggered
 * @remarks Web-only -- returns early on native platforms. Uses lazy-loaded jsPDF
 * to avoid adding to the main bundle. The generated PDF follows A4 portrait layout
 * with 40pt margins, matching the project's player report PDF styling.
 */
export async function generateScoutReportPdf(
  data: ScoutReportPdfData,
  filename = "Scout Report.pdf"
): Promise<void> {
  if (Platform.OS !== "web") {
    return;
  }

  // Dynamic require inside the Platform.OS === "web" guard ensures jsPDF is
  // only loaded at call time, not at module load. The bundler's tree-shaking
  // excludes this code path from native bundles via the Platform.OS guard above.

  const { jsPDF } = require("jspdf") as typeof import("jspdf");
  const pdf = new jsPDF("portrait", "pt", "a4");

  const y1 = drawHeader(pdf, data);
  const y2 = drawMatchDetails(pdf, data.matches, y1);
  const y3 = drawReportContent(pdf, data.reportText, y2);

  const y4 = data.numericRatings?.length
    ? drawNumericRatings(pdf, data.numericRatings, y3)
    : y3;

  if (data.overallScore != null) {
    drawOverallScore(pdf, data.overallScore, y4);
  }

  const pageCount =
    typeof (pdf as unknown as { getNumberOfPages: () => number })
      .getNumberOfPages === "function"
      ? (
          pdf as unknown as { getNumberOfPages: () => number }
        ).getNumberOfPages()
      : 1;
  addFooterToAllPages(pdf, pageCount, GEMINI_LOGO_DATA_URL);

  pdf.save(filename);
}

/* eslint-enable code-organization/enforce-statement-order -- End of procedural PDF generation code */
/* eslint-enable functional/no-let -- End of procedural PDF generation code */
